import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  FileText,
  BarChart3,
  Users,
  User,
  Trophy,
  Settings,
  Download,
  Calendar,
  TrendingUp,
  Target,
  Award
} from 'lucide-react';
import useStore from '@/store/useStore';
import { Report, CreateReportPayload } from '@/types/cricket';

interface ReportFormProps {
  report?: Report | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ReportForm = ({ report, onClose, onSuccess }: ReportFormProps) => {
  const { createReport, matches, players, teams, tournaments } = useStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<CreateReportPayload>({
    type: 'match',
    title: '',
    description: '',
    format: 'pdf',
    data: {}
  });
  const [selectedData, setSelectedData] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (report) {
      setFormData({
        type: report.type,
        title: report.title,
        description: report.description,
        format: report.format,
        data: report.data || {}
      });
      setSelectedData(report.data || {});
    }
  }, [report]);

  const handleInputChange = (field: keyof CreateReportPayload, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDataChange = (key: string, value: unknown) => {
    setSelectedData(prev => ({
      ...prev,
      [key]: value
    }));
    setFormData(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [key]: value
      }
    }));
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'match':
        return <BarChart3 className="h-4 w-4 text-blue-500" />;
      case 'player':
        return <User className="h-4 w-4 text-green-500" />;
      case 'team':
        return <Users className="h-4 w-4 text-purple-500" />;
      case 'tournament':
        return <Trophy className="h-4 w-4 text-yellow-500" />;
      case 'system':
        return <Settings className="h-4 w-4 text-gray-500" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'match':
        return 'bg-blue-100 text-blue-800';
      case 'player':
        return 'bg-green-100 text-green-800';
      case 'team':
        return 'bg-purple-100 text-purple-800';
      case 'tournament':
        return 'bg-yellow-100 text-yellow-800';
      case 'system':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getFormatColor = (format: string) => {
    switch (format) {
      case 'pdf':
        return 'bg-red-100 text-red-800';
      case 'csv':
        return 'bg-green-100 text-green-800';
      case 'json':
        return 'bg-blue-100 text-blue-800';
      case 'html':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim()) {
      return;
    }

    try {
      setIsSubmitting(true);
      
      // For now, we'll just create the report entry
      // In a real implementation, this would trigger a background job to generate the actual report
      await createReport(formData);
      
      onSuccess();
    } catch (error) {
      console.error('Error creating report:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDataOptions = () => {
    switch (formData.type) {
      case 'match':
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Match Selection</Label>
              <div className="mt-2 space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="all-matches"
                    checked={selectedData.includeAllMatches === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeAllMatches', checked)
                    }
                  />
                  <Label htmlFor="all-matches">Include all matches</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="completed-matches"
                    checked={selectedData.includeCompletedMatches === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeCompletedMatches', checked)
                    }
                  />
                  <Label htmlFor="completed-matches">Only completed matches</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="match-details"
                    checked={selectedData.includeMatchDetails === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeMatchDetails', checked)
                    }
                  />
                  <Label htmlFor="match-details">Include detailed match information</Label>
                </div>
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Statistics</Label>
              <div className="mt-2 space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="batting-stats"
                    checked={selectedData.includeBattingStats === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeBattingStats', checked)
                    }
                  />
                  <Label htmlFor="batting-stats">Batting statistics</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="bowling-stats"
                    checked={selectedData.includeBowlingStats === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeBowlingStats', checked)
                    }
                  />
                  <Label htmlFor="bowling-stats">Bowling statistics</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="fielding-stats"
                    checked={selectedData.includeFieldingStats === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeFieldingStats', checked)
                    }
                  />
                  <Label htmlFor="fielding-stats">Fielding statistics</Label>
                </div>
              </div>
            </div>
          </div>
        );

      case 'player':
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Player Selection</Label>
              <div className="mt-2 space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="all-players"
                    checked={selectedData.includeAllPlayers === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeAllPlayers', checked)
                    }
                  />
                  <Label htmlFor="all-players">Include all players</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="active-players"
                    checked={selectedData.includeActivePlayers === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeActivePlayers', checked)
                    }
                  />
                  <Label htmlFor="active-players">Only active players</Label>
                </div>
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Player Data</Label>
              <div className="mt-2 space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="career-stats"
                    checked={selectedData.includeCareerStats === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeCareerStats', checked)
                    }
                  />
                  <Label htmlFor="career-stats">Career statistics</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="recent-form"
                    checked={selectedData.includeRecentForm === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeRecentForm', checked)
                    }
                  />
                  <Label htmlFor="recent-form">Recent form analysis</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="performance-trends"
                    checked={selectedData.includePerformanceTrends === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includePerformanceTrends', checked)
                    }
                  />
                  <Label htmlFor="performance-trends">Performance trends</Label>
                </div>
              </div>
            </div>
          </div>
        );

      case 'team':
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Team Selection</Label>
              <div className="mt-2 space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="all-teams"
                    checked={selectedData.includeAllTeams === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeAllTeams', checked)
                    }
                  />
                  <Label htmlFor="all-teams">Include all teams</Label>
                </div>
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Team Data</Label>
              <div className="mt-2 space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="team-stats"
                    checked={selectedData.includeTeamStats === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeTeamStats', checked)
                    }
                  />
                  <Label htmlFor="team-stats">Team statistics</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="team-players"
                    checked={selectedData.includeTeamPlayers === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeTeamPlayers', checked)
                    }
                  />
                  <Label htmlFor="team-players">Team player roster</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="team-performance"
                    checked={selectedData.includeTeamPerformance === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeTeamPerformance', checked)
                    }
                  />
                  <Label htmlFor="team-performance">Team performance analysis</Label>
                </div>
              </div>
            </div>
          </div>
        );

      case 'tournament':
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Tournament Selection</Label>
              <div className="mt-2 space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="all-tournaments"
                    checked={selectedData.includeAllTournaments === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeAllTournaments', checked)
                    }
                  />
                  <Label htmlFor="all-tournaments">Include all tournaments</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="active-tournaments"
                    checked={selectedData.includeActiveTournaments === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeActiveTournaments', checked)
                    }
                  />
                  <Label htmlFor="active-tournaments">Only active tournaments</Label>
                </div>
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Tournament Data</Label>
              <div className="mt-2 space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="tournament-stats"
                    checked={selectedData.includeTournamentStats === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeTournamentStats', checked)
                    }
                  />
                  <Label htmlFor="tournament-stats">Tournament statistics</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="tournament-teams"
                    checked={selectedData.includeTournamentTeams === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeTournamentTeams', checked)
                    }
                  />
                  <Label htmlFor="tournament-teams">Participating teams</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="tournament-matches"
                    checked={selectedData.includeTournamentMatches === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeTournamentMatches', checked)
                    }
                  />
                  <Label htmlFor="tournament-matches">Tournament matches</Label>
                </div>
              </div>
            </div>
          </div>
        );

      case 'system':
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">System Data</Label>
              <div className="mt-2 space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="user-activity"
                    checked={selectedData.includeUserActivity === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeUserActivity', checked)
                    }
                  />
                  <Label htmlFor="user-activity">User activity logs</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="system-stats"
                    checked={selectedData.includeSystemStats === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeSystemStats', checked)
                    }
                  />
                  <Label htmlFor="system-stats">System statistics</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="error-logs"
                    checked={selectedData.includeErrorLogs === true}
                    onCheckedChange={(checked) => 
                      handleDataChange('includeErrorLogs', checked)
                    }
                  />
                  <Label htmlFor="error-logs">Error logs</Label>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getTypeIcon(formData.type)}
            {report ? 'Edit Report' : 'Generate Report'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Type and Format */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Report Type</Label>
              <Select 
                value={formData.type} 
                onValueChange={(value) => handleInputChange('type', value)}
                disabled={!!report}
              >
                <SelectTrigger id="type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="match">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-blue-500" />
                      Match Reports
                    </div>
                  </SelectItem>
                  <SelectItem value="player">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-green-500" />
                      Player Reports
                    </div>
                  </SelectItem>
                  <SelectItem value="team">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-purple-500" />
                      Team Reports
                    </div>
                  </SelectItem>
                  <SelectItem value="tournament">
                    <div className="flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-yellow-500" />
                      Tournament Reports
                    </div>
                  </SelectItem>
                  <SelectItem value="system">
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4 text-gray-500" />
                      System Reports
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="format">Output Format</Label>
              <Select 
                value={formData.format} 
                onValueChange={(value) => handleInputChange('format', value)}
                disabled={!!report}
              >
                <SelectTrigger id="format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF Document</SelectItem>
                  <SelectItem value="csv">CSV Spreadsheet</SelectItem>
                  <SelectItem value="json">JSON Data</SelectItem>
                  <SelectItem value="html">HTML Report</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Report Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Enter a descriptive title for your report"
              disabled={!!report}
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe what this report will contain..."
              rows={3}
              disabled={!!report}
              required
            />
          </div>

          {/* Data Options */}
          {!report && (
            <div className="space-y-2">
              <Label>Report Options</Label>
              <Card>
                <CardContent className="p-4">
                  {renderDataOptions()}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Preview */}
          <div className="space-y-2">
            <Label>Report Preview</Label>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  {getTypeIcon(formData.type)}
                  <Badge className={getTypeColor(formData.type)}>
                    {formData.type}
                  </Badge>
                  <Badge className={getFormatColor(formData.format)}>
                    {formData.format.toUpperCase()}
                  </Badge>
                </div>
                <h3 className="font-medium mb-2">{formData.title || 'Report title will appear here'}</h3>
                <p className="text-sm text-gray-600">
                  {formData.description || 'Report description will appear here'}
                </p>
                {Object.keys(selectedData).length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500">Selected options: {Object.keys(selectedData).join(', ')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="bg-cricket-green" disabled={isSubmitting}>
              {isSubmitting ? 'Generating...' : (report ? 'Update Report' : 'Generate Report')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReportForm; 